export interface StaticPickWallLocation {
  x: number;
  y: number;
  z: number;
}

export interface StaticPickWallGateResponse {
  location?: Partial<StaticPickWallLocation>;
  mappedPtlDeviceId?: string;
  isDisabled?: boolean;
}

export interface StaticPickWallResponse {
  pickWallId?: string;
  mappedPutWallId?: string;
  gates?: StaticPickWallGateResponse[];
}

export interface StaticPickWallSlot extends StaticPickWallLocation {
  id: string;
  position: string;
  pickWallId?: string;
  mappedPutWallId?: string;
  mappedPtlDeviceId?: string;
  isDisabled: boolean;
}

export function buildLocationKey(
  x?: number,
  y?: number,
  z?: number
): string | null {
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    typeof z !== "number"
  ) {
    return null;
  }

  return `${x},${y},${z}`;
}

export function matchesStaticSlot(
  slot: Pick<StaticPickWallSlot, "x" | "y" | "z">,
  location?: Partial<StaticPickWallLocation> | null
): boolean {
  return (
    buildLocationKey(slot.x, slot.y, slot.z) ===
    buildLocationKey(location?.x, location?.y, location?.z)
  );
}

export function flattenStaticPickWallSlots(
  walls: unknown
): StaticPickWallSlot[] {
  if (!Array.isArray(walls)) {
    return [];
  }

  const uniqueSlots = new Map<string, StaticPickWallSlot>();

  walls.forEach((wall: StaticPickWallResponse | null | undefined) => {
    const gates = Array.isArray(wall?.gates) ? wall?.gates : null;

    if (!gates) {
      return;
    }

    gates.forEach((gate) => {
      const position = buildLocationKey(
        gate?.location?.x,
        gate?.location?.y,
        gate?.location?.z
      );

      if (!position) {
        return;
      }

      uniqueSlots.set(position, {
        id: position,
        position,
        x: gate.location!.x as number,
        y: gate.location!.y as number,
        z: gate.location!.z as number,
        pickWallId: wall?.pickWallId,
        mappedPutWallId: wall?.mappedPutWallId,
        mappedPtlDeviceId: gate?.mappedPtlDeviceId,
        isDisabled: gate?.isDisabled === true,
      });
    });
  });

  return Array.from(uniqueSlots.values()).sort((left, right) => {
    if (left.z !== right.z) {
      return right.z - left.z;
    }

    if (left.x !== right.x) {
      return right.x - left.x;
    }

    return left.y - right.y;
  });
}
